import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject,
  DestroyRef
} from '@angular/core';
import { deepEqual, isNumber } from '@anecdotes/utils';
import { debounceTime, distinctUntilChanged, filter, map, switchMap, take } from 'rxjs/operators';
import { OverlapElementTree, SearchOverlap, SearchOverlapsFoundEvent } from '../../models';
import { DataFilterManagerService } from '../../services/data-filter-manager/data-filter-manager.service';
import { DataSearchComponent } from '../data-search/data-search.component';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { SearchInstancesManagerInternalService } from '../../services/search-instances-manager-internal/search-instances-manager-internal.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface OverlapIndexEntry {
  itemId: string;
  rowIndex: number;
  row: any;
  overlapIndex: number;
  overlapElementTree: OverlapElementTree;
}

@Component({
  selector: 'app-search-results-pagination',
  templateUrl: './search-results-pagination.component.html',
  styleUrls: ['./search-results-pagination.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchResultsPaginationComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly dataSearchInitialization$ = new Subject<void>();

  private overlaps: SearchOverlap[];
  private index: OverlapIndexEntry[] = [];
  private currentSearchScopeKey: string;
  private dataSearch: DataSearchComponent;
  private wasFirstOverlapFocused: boolean;
  private currentOverlapIndexEntry$ = new BehaviorSubject<OverlapIndexEntry>(null);

  get currentOverlapIndexEntry(): OverlapIndexEntry {
    return this.currentOverlapIndexEntry$.value;
  }
  set currentOverlapIndexEntry(value: OverlapIndexEntry) {
    this.currentOverlapIndexEntry$.next(value);
  }

  currentHighlightedResult: number;
  totalSearchResults: number;

  @Input()
  filterInstanceId: string;

  @Input()
  shouldBeDisplayedForEmptyResults = false;

  @Input()
  shouldShowControls = true;

  @Input()
  searchResultsTranslationKey = 'core.searchResults';

  @Input()
  clearSearchTranslationKey = 'core.clearSearch';

  @Output()
  dataFocusChange = new EventEmitter(true);

  @HostBinding('class.hidden')
  get isHidden(): boolean {
    return (!this.shouldBeDisplayedForEmptyResults || !this.dataSearch?.searchField?.value) && !this.totalSearchResults;
  }

  get isNavigationDisabled(): boolean {
    return this.shouldBeDisplayedForEmptyResults && !this.totalSearchResults;
  }

  get isPrevButtonDisabled(): boolean {
    return this.currentHighlightedResult === 0 || this.isNavigationDisabled;
  }

  get isNextButtonDisabled(): boolean {
    return this.currentHighlightedResult + 1 === this.totalSearchResults || this.isNavigationDisabled;
  }

  get currentPageNumber(): number {
    return this.currentHighlightedResult + 1 || 0;
  }

  constructor(
    private cd: ChangeDetectorRef,
    private searchInstancesManagerInternal: SearchInstancesManagerInternalService,
    private elementRef: ElementRef<HTMLElement>,
    private dataFilterManagerService: DataFilterManagerService
  ) {}

  ngOnInit(): void {
    this.currentSearchScopeKey = this.searchInstancesManagerInternal.getSearchScopeKey(this.elementRef.nativeElement);
    this.searchInstancesManagerInternal.addSearchResultsPaginator(this.currentSearchScopeKey, this);
    this.setupDataBasedSubscriptions();

    if (this.currentSearchScopeKey) {
      this.searchInstancesManagerInternal
        .getDataSearch(this.currentSearchScopeKey)
        .pipe(
          filter(Boolean),
          take(1),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe((dataSearch) => this.dataSearch = dataSearch);
    }

    this.currentOverlapIndexEntry$
      .pipe(
        distinctUntilChanged(
          (previousOverlap, currentOverlap) =>
            `${previousOverlap?.itemId}-${previousOverlap?.overlapIndex}` ===
            `${currentOverlap?.itemId}-${currentOverlap?.overlapIndex}`,
        ),
        filter((currentOverlap) => !!currentOverlap), takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.emitDataFocusChange());

    this.dataSearchInitialization$
      .pipe(
        switchMap(() => {
          return this.searchInstancesManagerInternal.getDataSearch(this.currentSearchScopeKey)
            .pipe(
              filter(Boolean),
              take(1),
            );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((dataSearch) => {
        this.dataSearch = dataSearch;
        this.dataSearch.reset();
      });
  }

  ngOnDestroy(): void {
    this.searchInstancesManagerInternal.removeSearchResultsPaginator(this.currentSearchScopeKey);
  }

  clearSearch(): void {
    if (this.filterInstanceId) {
      this.dataFilterManagerService.resetSearch(this.filterInstanceId);
    } else {
      this.executeDataSearch();
    }
  }

  goToNextSearchHighlight(): void {
    if (!this.isNextButtonDisabled) {
      const newIndex = this.currentHighlightedResult + 1;
      this.changeOverlapIndex(newIndex);
    }
  }

  goToPrevSearchHighlight(): void {
    if (!this.isPrevButtonDisabled) {
      const newIndex = this.currentHighlightedResult - 1;
      this.changeOverlapIndex(newIndex);
    }
  }

  private changeOverlapIndex(newIndex: number): void {
    this.currentOverlapIndexEntry$.next(this.index[newIndex]);
    this.currentHighlightedResult = newIndex;
  }

  private handleOverlapsFound(): void {
    const needToChangeHighlighting =
      !isNumber(this.currentHighlightedResult) || this.currentHighlightedResult + 1 >= this.index.length;

    if (needToChangeHighlighting) {
      this.currentHighlightedResult = -1;
      const currentIndex = this.currentHighlightedResult + 1;
      this.changeOverlapIndex(currentIndex);

      if (!this.wasFirstOverlapFocused) {
        this.wasFirstOverlapFocused = true;
      }
    }
  }

  private emitDataFocusChange(): void {
    this.dataFocusChange.emit(this.currentOverlapIndexEntry$.value.row);
  }

  private setupDataBasedSubscriptions(): void {
    this.getOverlapsFound()
      .pipe(
        debounceTime(500),
        distinctUntilChanged((prev, curr) => deepEqual(prev, curr)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((overlaps) => {
        this.overlaps = [];
        this.index = [];

        if (overlaps.length) {
          overlaps.forEach((overlap, rowIndex) => {
            for (let overlapIndex = 0; overlapIndex < overlap.overlapsCount; overlapIndex++) {
              this.overlaps.push(overlap);
              this.index.push({
                itemId: overlap.itemId,
                rowIndex,
                row: overlap.object,
                overlapIndex,
                overlapElementTree: overlap.overlapElementTree ? overlap.overlapElementTree[overlapIndex] : null,
              });
            }
          });

          this.totalSearchResults = this.overlaps.length;
          this.handleOverlapsFound();
        } else {
          this.currentOverlapIndexEntry$.next(undefined);
          delete this.totalSearchResults;
          delete this.currentHighlightedResult;
          delete this.wasFirstOverlapFocused;
        }

        this.cd.detectChanges();
      });
  }

  private getOverlapsFound(): Observable<SearchOverlap[]> {
    if (this.filterInstanceId) {
      return this.dataFilterManagerService.getSearchOverlaps(this.filterInstanceId).pipe(
        map((filterSearchOverlaps) => {
          const result: SearchOverlap[] = [];

          if (!filterSearchOverlaps) {
            return [];
          }

          filterSearchOverlaps.forEach((filterSearchOverlap) => {
            result.push({
              itemId: filterSearchOverlap.itemId,
              object: filterSearchOverlap.item,
              overlapsCount: filterSearchOverlap.propertyOverlaps.reduce((result, overlap) => result + overlap.overlapsCount, 0),
              overlapElementTree: filterSearchOverlap.propertyOverlaps.flatMap((overlap): OverlapElementTree[] => overlap.overlapElementTree),
            });
          });

          return result;
        })
      );
    } else {
      this.currentSearchScopeKey = this.searchInstancesManagerInternal.getSearchScopeKey(this.elementRef.nativeElement);
      return this.searchInstancesManagerInternal.getDataSearch(this.currentSearchScopeKey).pipe(
        filter(Boolean),
        take(1),
        switchMap((dataSearch: DataSearchComponent) => {
          return dataSearch.overlapsFound.pipe(
            map((searchOverlapsEvent: SearchOverlapsFoundEvent) => searchOverlapsEvent.overlaps)
          );
        })
      );
    }
  }

  private executeDataSearch(): void {
    if (!this.dataSearch) {
      if (!this.currentSearchScopeKey) {
        this.currentSearchScopeKey = this.searchInstancesManagerInternal.getSearchScopeKey(
          this.elementRef.nativeElement
        );
      }

      this.dataSearchInitialization$.next();
    } else {
      this.dataSearch.reset();
    }
  }
}
